import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { QualityMeasureSurveyService } from '@services/quality-measure-survey.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { QualityMeasureSurveyActions } from '@store/actions/quality-measure-survey/quality-measure-survey.actions';
import { downloadBlob } from '../../../utils/blob-viewer.util';

@Injectable()
export class QualityMeasureSurveyEffects {
  loadSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QualityMeasureSurveyActions.loadSurvey),
      switchMap(({ organizationId }) =>
        this.qualityMeasureSurveyService.getSurvey(organizationId).pipe(
          map((survey) => QualityMeasureSurveyActions.loadSurveySuccess({ survey })),
          catchError((error) =>
            of(QualityMeasureSurveyActions.loadSurveyFailure({
              error: error.message || 'Failed to load quality measure survey',
            }))
          )
        )
      )
    )
  );

  loadSurveyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QualityMeasureSurveyActions.loadSurveyFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to load the quality measure survey. Please try again.',
            'Quality Measure Survey Error'
          );
        })
      ),
    { dispatch: false }
  );

  saveSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QualityMeasureSurveyActions.saveSurvey),
      switchMap(({ organizationId, answers }) =>
        this.qualityMeasureSurveyService.saveSurvey(organizationId, answers).pipe(
          map(() => QualityMeasureSurveyActions.saveSurveySuccess({ organizationId })),
          catchError((error) =>
            of(QualityMeasureSurveyActions.saveSurveyFailure({
              error: error.message || 'Failed to save quality measure survey',
            }))
          )
        )
      )
    )
  );

  // The server auto-clears sibling radio options and mirrors sister-measure answers on save —
  // re-fetching keeps the client's optimistic UI in sync with that server-side behavior.
  reloadSurveyAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QualityMeasureSurveyActions.saveSurveySuccess),
      map(({ organizationId }) => QualityMeasureSurveyActions.loadSurvey({ organizationId }))
    )
  );

  saveSurveyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QualityMeasureSurveyActions.saveSurveyFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to save the quality measure survey. Please try again.',
            'Quality Measure Survey Error'
          );
        })
      ),
    { dispatch: false }
  );

  exportSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QualityMeasureSurveyActions.exportSurvey),
      switchMap(({ organizationId }) =>
        this.qualityMeasureSurveyService.exportSurvey(organizationId).pipe(
          map(({ blob, fileName }) =>
            QualityMeasureSurveyActions.exportSurveySuccess({
              blob,
              filename: fileName,
            })
          ),
          catchError((error) =>
            of(QualityMeasureSurveyActions.exportSurveyFailure({
              error: error.message || 'Failed to export quality measure survey',
            }))
          )
        )
      )
    )
  );

  exportSurveySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QualityMeasureSurveyActions.exportSurveySuccess),
        tap(({ blob, filename }) => downloadBlob(blob, filename))
      ),
    { dispatch: false }
  );

  exportSurveyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(QualityMeasureSurveyActions.exportSurveyFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to export the quality measure survey. Please try again.',
            'Quality Measure Survey Error'
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private qualityMeasureSurveyService: QualityMeasureSurveyService,
    private errorDialogService: ErrorDialogService
  ) {}
}
